import React, { useRef, useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { useNavigate } from 'react-router-dom';
import AdminNavTabs from '../components/AdminNavTabs';
// Shared admin chrome (.admin-dashboard themed background, .admin-header, .admin-tabs,
// .btn-* ). Audit previously imported only AdminAudit.css, so on a direct load of
// /admin/audit the themed `.admin-dashboard` background rule was absent and the page
// fell back to the body color — the "some tabs don't show the background" bug.
import './AdminDashboard.css';
import './AdminAudit.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';

const AdminAudit = () => {
  const navigate = useNavigate();
  const statusRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');
  const [username, setUsername] = useState('');

  useEffect(() => {
    if (!error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [error]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Audit');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        // Optional: default filter to current username
        const ident = user.username || user.attributes?.email || '';
        setUsername(ident);
        setUsernameFilter(ident);
        await fetchLogs(ident);
      } catch (err) {
        console.error('Auth error (audit):', err);
        navigate('/admin/login');
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    init();
  }, []);

  const fetchLogs = async (filterUsername) => {
    try {
      setLoading(true);
      setError('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const baseUrl = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL;
      const url = new URL(`${baseUrl}/admin/audit`);
      if (filterUsername) {
        url.searchParams.set('username', filterUsername);
      }

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load audit log');
      }

      const data = await response.json();
      setLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = (e) => {
    e.preventDefault();
    fetchLogs(usernameFilter.trim());
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch (logoutError) {
      console.error('Logout error:', logoutError);
      setError('Failed to log out');
    }
  };

  if (loading && !logs.length && !error) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading audit log...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-clipboard-list"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Audit Log</h1>
            <p>Review recent admin actions</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {error && (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        <div className="products-list-container">
          <h2>Recent Activity</h2>

          <form className="admin-audit-filters" onSubmit={handleApplyFilter}>
            <div className="form-row">
              <div className="form-group">
                <label>Username (optional)</label>
                <input
                  type="text"
                  value={usernameFilter}
                  onChange={(e) => setUsernameFilter(e.target.value)}
                  placeholder="Filter by username"
                />
              </div>
              <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                <button type="submit" className="btn-secondary">
                  <i className="fas fa-filter"></i>
                  Apply Filter
                </button>
              </div>
            </div>
          </form>

          {loading ? (
            <div className="admin-loading" style={{ minHeight: '120px' }}>
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading audit log...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-clipboard-list"></i>
              <p>No audit entries found</p>
            </div>
          ) : (
            <div className="audit-table-wrapper">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>Time (UTC)</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Entity</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => {
                    const rawDetails = log.details;
                    const safeDetails = rawDetails && typeof rawDetails === 'object' ? rawDetails : null;
                    const productName = safeDetails && safeDetails.productName
                      ? safeDetails.productName
                      : '';
                    const category = safeDetails && safeDetails.category
                      ? safeDetails.category
                      : '';

                    const changedFields =
                      safeDetails && safeDetails.changedFields && typeof safeDetails.changedFields === 'object'
                        ? safeDetails.changedFields
                        : null;

                    const hasPrettyDiff = !!changedFields && Object.keys(changedFields).length > 0;

                    // For JSON fallback view, avoid repeating productName
                    const detailsForJson = safeDetails
                      ? (() => {
                          const shallow = { ...safeDetails };
                          if ('productName' in shallow) {
                            delete shallow.productName;
                          }
                          return shallow;
                        })()
                      : null;

                    return (
                      <tr key={`${log.pk || ''}-${log.sk || idx}`}>
                        <td>{log.createdAt || (log.sk || '').replace('TS#', '')}</td>
                        <td>{log.username || ''}</td>
                        <td>{log.action || ''}</td>
                        <td>
                          <div className="audit-entity">
                            {log.entityType && (
                              <span className="audit-entity-type">{log.entityType}</span>
                            )}
                            {productName && (
                              <span className="audit-entity-name">{productName}</span>
                            )}
                            {category && (
                              <span className="audit-entity-category">{category}</span>
                            )}
                            {log.entityId && (
                              <span className="audit-entity-id">({log.entityId})</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {!safeDetails ? (
                            <span style={{ color: '#777' }}>—</span>
                          ) : hasPrettyDiff ? (
                            <div className="audit-pretty-details">
                              <div className="audit-pretty-summary">
                                <span className="audit-pill audit-pill-muted">
                                  {Object.keys(changedFields).length} field
                                  {Object.keys(changedFields).length > 1 ? 's' : ''} changed
                                </span>
                              </div>
                              <ul className="audit-diff-list">
                                {Object.entries(changedFields).map(([field, value]) => {
                                  const oldVal = value && typeof value === 'object' ? value.old : undefined;
                                  const newVal = value && typeof value === 'object' ? value.new : undefined;
                                  return (
                                    <li key={field} className="audit-diff-item">
                                      <span className="audit-diff-field">{field}</span>
                                      <span className="audit-diff-arrow">→</span>
                                      <span className="audit-diff-old">{String(oldVal ?? '')}</span>
                                      <span className="audit-diff-new">{String(newVal ?? '')}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ) : (
                            <code className="audit-details">
                              {JSON.stringify(detailsForJson || safeDetails)}
                            </code>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAudit;
